/**
 * examOutbox — the DSAT exam autosave OUTBOX (R1.1).
 *
 * THE PROMISE: a student's answer/flag is NEVER silently lost. Every mutation is
 * (a) held in a durable, user-scoped localStorage queue that survives a reload,
 * (b) sent with bounded retry until it lands, and (c) — if the module it belongs
 * to has closed under it — surfaced in a warm recovery state, never dropped
 * unseen. This is the frontend half of the R1.1 contract; the backend is the
 * second net (EXAM_STALE_MODULE / EXAM_STALE_SEQ machine codes on the answer/flag
 * routes).
 *
 * This module is FRAMEWORK-AGNOSTIC (no React) so the queue/persistence/retry
 * logic is unit-testable in isolation; useExamOutbox.js binds it to ExamShell.
 *
 * ── The five adversarial-review requirements it implements ────────────────────
 *  1. RECORD IDENTITY: each record keys on user/session/section/module/position/
 *     type and carries a per-item monotonic `clientSeq` (persisted). A re-answer
 *     of the same slot COALESCES (replaces) into one record with a higher seq.
 *     (The F13 `time` record kind is the ONE exception — see below.)
 *  2. SERVER-AUTHORITATIVE EXPIRY: the outbox never blocks module advance / timer
 *     expiry. Flushing only sends records matching the still-open (section,
 *     module); a record whose module has closed transitions to a warm, accessible
 *     RECOVERY state ("couldn't be submitted before time expired") and is removed
 *     from the queue (so it never blocks navigation).
 *  3. MULTI-TAB: the OWNING tab flushes (gated by the caller via `canFlush`); a
 *     secondary tab renders read-only and never enqueues. The server-side
 *     client_seq is the second net if two tabs ever race.
 *  4. FALLIBLE STORAGE: a record is announced queued-DURABLY only after a verified
 *     localStorage write (write-then-read-back). On quota/denied/malformed the
 *     record is KEPT in memory (never lost) and the caller shows a warm "keep this
 *     tab open" message. Malformed stored JSON on load is discarded safely.
 *  5. PRIVACY (minors, shared devices): keys are user-scoped (user id in the key)
 *     + schema-versioned (v1). On load, any stored outbox whose user id ≠ the
 *     authenticated user is purged UNREAD. Callers purge on logout / terminal
 *     session / successful flush.
 *
 * ── The THIRD record kind: `time` (F13 PR1 — per-item active time) ────────────
 * A `time` record carries a MONOTONIC per-item total (ms) instead of a student
 * value, and rides the SAME queue: same record key (so it coalesces per slot),
 * same durable persistence, same ownership gate, same recovery on a closed
 * module. Three deliberate differences, each load-bearing:
 *
 *   • NO `clientSeq`. The server merges `max(stored, clamp(incoming))`, which is
 *     commutative + idempotent — a duplicate/reordered/multi-device flush is a
 *     provable no-op, so an ordering counter would add nothing and could only
 *     DROP a higher (better) total from another device.
 *
 *   • BEST-EFFORT, AND STRICTLY SUBORDINATE TO WORK. This is the invariant the
 *     whole record kind hangs on: a time record must NEVER gate, delay, or abort
 *     the delivery of a student's answer. Three mechanisms enforce it, and all
 *     three are load-bearing — (1) seq-less records sort LAST in a drain pass, so
 *     answers always go first; (2) a FAILED time POST is DROPPED, never retried
 *     and never backed off, so it cannot abort the pass that still holds queued
 *     answers behind it; (3) it never counts toward `pendingForOpen`. Dropping is
 *     free precisely because the value is a MONOTONIC TOTAL: the next emit for
 *     that item already contains everything the dropped one carried, so the data
 *     self-heals. The concrete failure this prevents: an end-of-module review pass
 *     emits enough item-time POSTs to trip the route's rate limit, the 429 backs
 *     the whole queue off, the student's queued answers never send, the finish
 *     gate holds until the timer expires, and their answer surfaces in the
 *     recovery banner instead of their score. Telemetry must never cost a student
 *     an answer.
 *
 *   • NOT counted by `pendingForOpen`. That number gates the runner's "finish
 *     module" drain: it exists so a module never closes before the student's own
 *     WORK has saved. Telemetry must never hold that gate — and a `time` record
 *     re-enqueues every ~15s while the student sits on the finish screen, so an
 *     offline student whose queue includes one would wait FOREVER (a live-lock,
 *     not a delay). Answers/flags still gate it exactly as before.
 *
 *   • Enqueued value is a TOTAL, never a delta, so a lost flush self-heals on the
 *     next one instead of losing time (at-least-once delivery is all the outbox
 *     promises).
 */

// Schema-versioned, user-scoped storage-key prefix. Bumping `v1` orphans (and the
// prefix purge below reaps) every old-schema blob — the clean migration path.
export const OUTBOX_KEY_PREFIX = 'dsat.exam.outbox.v1.';

// encodeURIComponent does NOT escape '.', so we escape it explicitly (%2E) to keep
// the single '.' key separator unambiguous even for a dotted id (M6).
function encodeSeg(s) {
  return encodeURIComponent(String(s)).replace(/\./g, '%2E');
}

/** The localStorage key for one (user, session) outbox: `<prefix><encUser>.<encSession>`.
 *  The user id in the key is a privacy boundary (requirement 5): a different user
 *  never resolves to another user's queue. Both ids are encoded (incl. '.') so a
 *  '.' inside an id can't corrupt the segment-parse purge (M6) — an encoded value
 *  never contains '.', so the single '.' separator is unambiguous. */
export function outboxStorageKey(userId, sessionId) {
  return `${OUTBOX_KEY_PREFIX}${encodeSeg(userId)}.${encodeSeg(sessionId)}`;
}

/** Extract the (decoded) owner user id from an outbox storage KEY, WITHOUT reading
 *  its value (M6 privacy: never JSON.parse a foreign user's blob — that would load
 *  another minor's answers into memory). Returns null for a non-outbox or
 *  malformed key (→ the caller purges it unread). */
export function ownerUserIdFromKey(key) {
  if (typeof key !== 'string' || !key.startsWith(OUTBOX_KEY_PREFIX)) return null;
  const suffix = key.slice(OUTBOX_KEY_PREFIX.length);
  const dot = suffix.indexOf('.');
  if (dot <= 0) return null; // no user segment → malformed
  try {
    return decodeURIComponent(suffix.slice(0, dot));
  } catch {
    return null; // undecodable → treat as foreign/malformed
  }
}

/** The machine `error.code` from an axios error's envelope, or null. The outbox
 *  branches on the CODE (never the human message) — see the backend ErrorCode
 *  EXAM_* table. */
export function machineCode(err) {
  return err?.response?.data?.error?.code || null;
}

// The 409 codes that mean "this mutation targets a module/window that has CLOSED"
// → the entry couldn't be saved in time → RECOVER it (surface the warm banner),
// never retry it forever.
const STALE_MODULE_CODES = new Set([
  'EXAM_STALE_MODULE',
  'EXAM_WINDOW_CLOSED',
  'EXAM_NO_OPEN_MODULE',
]);
// The 409 code that means "a newer mutation to this slot already won" → the entry
// is correctly SUPERSEDED → drop it SILENTLY (not a failure to surface).
const STALE_SEQ_CODE = 'EXAM_STALE_SEQ';

// Bounded exponential backoff for transient (network / 5xx) failures.
const BACKOFF_BASE_MS = 1000;
const BACKOFF_MAX_MS = 30000;

/** Enumerate a Storage's keys portably: the standard ``length`` + ``key(i)``
 *  interface (real + jsdom localStorage), falling back to ``Object.keys`` for a
 *  plain-object fake. Snapshots into an array so callers can safely mutate the
 *  store while iterating. */
function storageKeys(store) {
  if (store && typeof store.length === 'number' && typeof store.key === 'function') {
    const keys = [];
    for (let i = 0; i < store.length; i += 1) {
      const k = store.key(i);
      if (k != null) keys.push(k);
    }
    return keys;
  }
  try {
    return Object.keys(store);
  } catch {
    return [];
  }
}

// The per-item, PER-FIELD sequence key (audit H3): answer and flag advance
// SEPARATE counters, so a flag never bumps the answer's high-water mark (which
// would let the server reject a legitimate, never-superseded answer). This is the
// same shape as the record key — one seq counter per (slot, type).
function seqKeyOf(section, module, position, type) {
  return `${section}:${module}:${position}:${type}`;
}

function recordKeyOf(section, module, position, type) {
  return `${section}:${module}:${position}:${type}`;
}

// The record kinds the outbox accepts. `time` (F13) is the MAX-MERGED, seq-less
// active-time report; `answer`/`flag` are the R1.1 last-write-wins mutations.
const RECORD_TYPES = new Set(['answer', 'flag', 'time']);

/** The server's own sanity ceiling on a reported active-time total (24h in ms —
 *  `_MAX_REPORTED_ACTIVE_MS` in routes/prep_exam.py). Mirrored here so a client
 *  never QUEUES a record the server is guaranteed to 422: a value outside [0, this]
 *  is clamped at the enqueue, and a stored blob carrying one is rejected on load.
 *  The real bound on what is CREDITED is far tighter (the server's per-item and
 *  per-module clamps); this only keeps garbage out of the durable queue. */
export const MAX_REPORTED_ACTIVE_MS = 24 * 60 * 60 * 1000;
// Record kinds that represent the STUDENT'S OWN WORK, and therefore gate the
// runner's finish-module drain (`pendingForOpen`). `time` is deliberately absent:
// telemetry must never be able to hold a module open (see the header note).
const WORK_RECORD_TYPES = new Set(['answer', 'flag']);

/**
 * Create an outbox bound to one (user, session).
 *
 * @param {object} opts
 * @param {string} opts.userId       authenticated user id (key scope + privacy).
 * @param {string} opts.sessionId    exam session id.
 * @param {(record) => Promise} opts.send  performs ONE mutation POST. Receives a
 *        record { type:'answer'|'flag', section, module, position, value, clientSeq };
 *        resolves on 2xx, rejects with the axios error (the outbox reads its
 *        machine code). Injected so the queue logic is testable without axios.
 * @param {(snapshot) => void} [opts.onChange]  called on every state change with
 *        getSnapshot()'s value (drives the React chip/banner).
 * @param {() => boolean} [opts.isOwner]  returns false on a SECONDARY tab that
 *        does not own mutations. Gates BOTH flushing AND every storage WRITE — a
 *        non-owner must never clobber the shared key with its (stale/empty) state
 *        (audit H1). Default: always owner (single-tab / no coordination).
 * @param {Storage} [opts.storage]   defaults to window.localStorage (injectable).
 * @param {() => number} [opts.now]  ms clock (injectable for tests).
 * @param {(fn, ms) => any} [opts.setTimer] / [opts.clearTimer]  scheduler (jest
 *        fake-timer friendly). Default window.setTimeout / clearTimeout.
 */
export function createExamOutbox({
  userId,
  sessionId,
  send,
  onChange,
  isOwner,
  storage,
  now,
  setTimer,
  clearTimer,
}) {
  const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  const clock = now || (() => Date.now());
  const schedule = setTimer || ((fn, ms) => setTimeout(fn, ms));
  const unschedule = clearTimer || ((h) => clearTimeout(h));
  const storageKey = outboxStorageKey(userId, sessionId);
  // Ownership gate: a non-owner never flushes AND never writes the shared storage
  // key (audit H1). Absent → this tab owns (single-tab / no coordination).
  const owns = typeof isOwner === 'function' ? isOwner : () => true;

  // ── In-memory state (localStorage is the durability mirror, not the truth) ──
  let records = new Map();          // recordKey -> { type, section, module, position, value, clientSeq, createdAt }
  let seqByItem = new Map();        // seqKey -> highest clientSeq ASSIGNED for that (slot, field)
  // R3-H3 — the DIRTY OVERLAY: records + recovery whose VERIFIED persist FAILED
  // (quota / requirement 4), so they live ONLY in memory, not in storage. Kept
  // separately so a demote→re-promote reconcile (which reloads from storage) can
  // OVERLAY them back instead of silently discarding the student's latest edit. An
  // entry clears only on a verified persist OR a confirmed server send.
  let dirtyRecords = new Map();     // recordKey -> record (undurable)
  let dirtyRecovery = [];           // [{ section, module, position, type }] (undurable)
  // R4-2 — the keys CHANGED since the last verified persist. Only these enter the
  // overlay on a failed persist (an unchanged, already-durable record must NEVER
  // re-enter the overlay and clobber a newer stored value on a later reconcile).
  let changedKeys = new Set();      // record keys mutated since the last durable snapshot
  let recoveryDirty = false;        // recovery changed since the last durable snapshot
  let serverOpen = null;            // { status, section, module } — last known server state
  // Records that couldn't be saved before their module closed — PERSISTED (audit
  // H4) so the warm notice survives a reload + the terminal screen, until the
  // student acknowledges. Minimal identity only (no answer value): a closed
  // module can't accept it anyway, and we minimize at-rest PII.
  let recoveryItems = [];           // [{ section, module, position, type }]
  let storageOk = true;            // did the last persist verify? (false => in-memory-only + warm message)
  let sending = false;              // a flush is in flight (no concurrent flush)
  let backoffMs = 0;                // current backoff (0 => not backing off)
  let retryTimer = null;            // scheduled retry handle
  let disposed = false;

  // ── Fallible storage (requirement 4): verified write, safe read, prefix purge ─

  function verifiedWrite(key, obj) {
    if (!store) return false;
    let serialized;
    try {
      serialized = JSON.stringify(obj);
    } catch {
      return false;
    }
    try {
      store.setItem(key, serialized);
      // Read-back verification: a quota-exceeded or private-mode write can throw
      // OR silently no-op; only a matching read-back proves durability.
      return store.getItem(key) === serialized;
    } catch {
      return false;
    }
  }

  function purgeOtherUsersOutboxes() {
    // Privacy (requirement 5 + audit M6): on a shared device, reap any v1 outbox
    // whose user id ≠ the authenticated user — UNREAD. Ownership is derived from
    // the KEY ALONE (never getItem/JSON.parse a foreign blob — that would load
    // another minor's answers into this session's memory, violating the "unread"
    // contract). A foreign OR malformed key is removed without ever reading it;
    // getItem only ever runs on the authenticated user's own exact key (in load()).
    if (!store) return;
    const me = String(userId);
    for (const key of storageKeys(store)) {
      if (typeof key !== 'string' || !key.startsWith(OUTBOX_KEY_PREFIX)) continue;
      const owner = ownerUserIdFromKey(key);
      if (owner === null || owner !== me) {
        try { store.removeItem(key); } catch { /* best-effort */ }
      }
    }
  }

  function validRecord(rec) {
    if (!rec || typeof rec !== 'object') return false;
    if (!RECORD_TYPES.has(rec.type)) return false;
    if (typeof rec.position !== 'number') return false;
    if (rec.type === 'time') {
      // A time record carries a monotonic total instead of a seq (see header).
      // The RANGE is validated on rehydration too, not just at enqueue: a stored
      // blob is untrusted input (hand-edited, corrupted, or written by an older
      // build), and a negative/oversized total would be a record guaranteed to 422
      // forever. Dropping it here keeps it out of the queue entirely.
      return (
        typeof rec.value === 'number'
        && Number.isFinite(rec.value)
        && rec.value >= 0
        && rec.value <= MAX_REPORTED_ACTIVE_MS
      );
    }
    return typeof rec.clientSeq === 'number';
  }

  function validRecovery(r) {
    return (
      r && typeof r === 'object'
      && RECORD_TYPES.has(r.type)
      && typeof r.position === 'number'
    );
  }

  /** Read + validate THIS user's own blob (the ONLY getItem the outbox ever does
   *  on a possibly-untrusted key — M6: never read a foreign key). Malformed or
   *  foreign-under-our-own-key → removed, returns null. */
  function readOwnBlob() {
    if (!store) return null;
    let raw;
    try { raw = store.getItem(storageKey); } catch { return null; }
    if (!raw) return null;
    let blob;
    try {
      blob = JSON.parse(raw);
    } catch {
      // Malformed stored JSON (requirement 4) → discard safely, never crash.
      try { store.removeItem(storageKey); } catch { /* best-effort */ }
      return null;
    }
    if (!blob || String(blob.userId) !== String(userId) || blob.v !== 1) {
      try { store.removeItem(storageKey); } catch { /* best-effort */ }
      return null;
    }
    return blob;
  }

  function load() {
    purgeOtherUsersOutboxes();
    const blob = readOwnBlob();
    if (!blob) return;
    for (const [key, rec] of Object.entries(blob.records || {})) {
      if (validRecord(rec)) records.set(key, rec);
    }
    for (const [key, seq] of Object.entries(blob.seq || {})) {
      if (typeof seq === 'number' && seq >= 0) seqByItem.set(key, seq);
    }
    if (Array.isArray(blob.recovery)) {
      recoveryItems = blob.recovery.filter(validRecovery);
    }
  }

  /** On ownership PROMOTION (secondary → owner, audits H1 + R2-M4 + R3-H3):
   *  ADOPT the latest STORAGE snapshot (never merge a secondary's possibly-stale,
   *  already-flushed in-memory records — that would resurrect saved records as
   *  false recovery, R2-M4), THEN overlay the DIRTY entries — a FORMER owner's
   *  mutations whose verified persist FAILED (quota) live only in memory, and must
   *  survive a demote→re-promote instead of being silently reverted to storage's
   *  older value (R3-H3). Dirty seqs are re-bumped above any adopted high-water. */
  function reconcileFromStorage() {
    const dirtyR = dirtyRecords;      // preserve the undurable overlay across the wipe
    const dirtyRec = dirtyRecovery;
    records = new Map();              // REPLACE records (R2-M4: never resurrect flushed)
    recoveryItems = [];              // REPLACE recovery (from storage below)
    // R5-1 — seq high-water marks are MONOTONIC: MAX-merge, NEVER wipe. A mount
    // /state seed applied while ownership was unresolved must survive this
    // reconcile (a wipe would let the next answer draw seq 1 and be silently
    // dropped EXAM_STALE_SEQ). seqByItem is deliberately NOT reset.
    const blob = readOwnBlob();
    if (blob) {
      for (const [key, rec] of Object.entries(blob.records || {})) {
        if (validRecord(rec)) records.set(key, rec);
      }
      for (const [key, seq] of Object.entries(blob.seq || {})) {
        if (typeof seq === 'number' && seq > (seqByItem.get(key) || 0)) seqByItem.set(key, seq);
      }
      if (Array.isArray(blob.recovery)) recoveryItems = blob.recovery.filter(validRecovery);
    }
    // Overlay the dirty (memory-only) records OVER the storage snapshot.
    for (const [key, rec] of dirtyR) {
      if (rec.type === 'time') {
        // A time record has no seq to renumber (max-merge needs no ordering) —
        // overlay it verbatim. Its value is a monotonic TOTAL, so even if storage
        // held a newer one, re-sending the older total is a server-side no-op.
        records.set(key, { ...rec });
        continue;
      }
      const sKey = seqKeyOf(rec.section, rec.module, rec.position, rec.type);
      const hw = seqByItem.get(sKey) || 0;
      const seq = rec.clientSeq <= hw ? hw + 1 : rec.clientSeq;
      seqByItem.set(sKey, Math.max(hw, seq));
      records.set(key, { ...rec, clientSeq: seq });
    }
    for (const r of dirtyRec) {
      if (
        validRecovery(r)
        && !recoveryItems.some(
          (x) => x.section === r.section && x.module === r.module
            && x.position === r.position && x.type === r.type,
        )
      ) {
        recoveryItems.push(r);
      }
    }
    // The overlay is still undurable until the next verified persist re-snapshots it
    // — track its keys as changed so a subsequent failed persist keeps them dirty
    // (R4-2), and re-materialize the overlay from records so its seqs match.
    changedKeys = new Set(dirtyR.keys());
    recoveryDirty = dirtyRec.length > 0;
    dirtyRecords = new Map();
    for (const k of changedKeys) {
      const rec = records.get(k);
      if (rec) dirtyRecords.set(k, rec);
    }
    dirtyRecovery = dirtyRec.map((r) => ({ ...r }));
  }

  // Rebuild the dirty overlay from the keys CHANGED since the last verified persist
  // (a persist just failed → those changed values are NOT in storage), or clear it
  // (a persist just succeeded → memory == storage). R3-H3 + R4-2.
  function markDirty(failed) {
    if (!failed) {
      dirtyRecords = new Map();
      dirtyRecovery = [];
      changedKeys = new Set();
      recoveryDirty = false;
      return;
    }
    // R4-2: ONLY changed keys are dirty. An unchanged, already-durable record is
    // NEVER re-added — so it can't overwrite a newer stored value on a reconcile.
    dirtyRecords = new Map();
    for (const k of changedKeys) {
      const rec = records.get(k);
      if (rec) dirtyRecords.set(k, rec);
    }
    dirtyRecovery = recoveryDirty ? recoveryItems.map((r) => ({ ...r })) : [];
  }

  function persist() {
    // A non-owner NEVER writes the shared key (audit H1): its (often empty/stale)
    // state must not clobber the owner's queued answers.
    if (!owns()) return false;
    if (records.size === 0 && recoveryItems.length === 0) {
      // Nothing to keep → remove the key rather than persist an empty blob (this
      // is the clean-flush cleanup that replaces a blind terminal purge). The
      // in-memory seq high-water marks survive for this session; a later reload
      // re-seeds them from the server manifest.
      try { if (store) store.removeItem(storageKey); } catch { /* best-effort */ }
      storageOk = true;
      markDirty(false);
      return true;
    }
    const blob = {
      v: 1,
      userId: String(userId),
      sessionId: String(sessionId),
      records: Object.fromEntries(records),
      seq: Object.fromEntries(seqByItem),
      recovery: recoveryItems,
    };
    storageOk = verifiedWrite(storageKey, blob);
    // R3-H3: a failed verified write → the queue is dirty (memory-only); a success
    // → everything is now durable.
    markDirty(!storageOk);
    return storageOk;
  }

  // ── Snapshot + notify ───────────────────────────────────────────────────────

  // Unsaved STUDENT WORK for the currently-open module. Drives the runner's
  // finish-module drain gate, so it counts answers/flags ONLY — a `time` record
  // must never keep a module open (F13: it re-enqueues every ~15s, so an offline
  // student would be gated forever, and losing a few seconds of telemetry to a
  // module close is not a loss worth trapping a student for).
  function pendingForOpen() {
    if (!serverOpen || serverOpen.status !== 'in_progress') return 0;
    let n = 0;
    for (const rec of records.values()) {
      if (
        WORK_RECORD_TYPES.has(rec.type)
        && rec.section === serverOpen.section
        && rec.module === serverOpen.module
      ) n += 1;
    }
    return n;
  }

  function getSnapshot() {
    const size = records.size;
    // The save CHIP speaks about the student's WORK ("Saving…" / "Saving paused"),
    // so its status counts answers/flags only. A `time` record enqueues on every
    // navigation and would otherwise strobe the chip through a timed exam — and
    // say "saving" when everything the student did is already safely stored.
    // `pendingCount` stays the TRUE queue depth (diagnostics + tests).
    let workPending = 0;
    for (const rec of records.values()) {
      if (WORK_RECORD_TYPES.has(rec.type)) workPending += 1;
    }
    // 'idle' when no work is queued; 'paused' when a transient failure is backing
    // off; 'saving' while queued and actively (re)trying.
    const status = workPending === 0 ? 'idle' : (backoffMs > 0 ? 'paused' : 'saving');
    return {
      status,
      pendingCount: size,
      pendingForOpen: pendingForOpen(),
      recovery: recoveryItems.length ? { count: recoveryItems.length } : null,
      storageOk,
    };
  }

  function notify() {
    if (onChange) onChange(getSnapshot());
  }

  // ── Sequence assignment (requirement 1) ─────────────────────────────────────

  function nextSeq(section, module, position, type) {
    const key = seqKeyOf(section, module, position, type);
    const next = (seqByItem.get(key) || 0) + 1;
    seqByItem.set(key, next);
    return next;
  }

  // Seed the per-field high-water marks from the server's manifest answer_seq /
  // flag_seq on resume, so the NEXT mutation's seq always exceeds what the server
  // already applied FOR THAT FIELD — a cross-device / cleared-storage resume never
  // collides into a spurious EXAM_STALE_SEQ (requirement 1 + the backend manifest
  // contract). Answer and flag seed INDEPENDENTLY (audit H3).
  //
  // R2-H2 — RENUMBER a stale QUEUED record so it lands. A record was assigned its
  // seq at enqueue time against the THEN-known high-water; if another device has
  // since advanced the server's per-field seq past it, replaying it verbatim would
  // be rejected EXAM_STALE_SEQ and SILENTLY dropped — losing the latest local human
  // intent. A queued record IS by construction the latest local intent (coalescing
  // leaves at most one record per (item,type)), so we bump its seq above the
  // server's high-water: reconnect-order last-writer-wins. If its value already
  // equals the server's, the re-send is a harmless idempotent overwrite. RESIDUAL:
  // a truly simultaneous same-field cross-device conflict resolves by reconnect
  // order (no per-mutation identity / device-epoch machinery, by design).
  function seedFromServerItems(items) {
    if (!Array.isArray(items) || serverOpen == null) return;
    const { section, module } = serverOpen;
    for (const it of items) {
      if (!it || typeof it.position !== 'number') continue;
      for (const [type, field] of [['answer', 'answer_seq'], ['flag', 'flag_seq']]) {
        const serverSeq = typeof it[field] === 'number' ? it[field] : 0;
        const key = seqKeyOf(section, module, it.position, type);
        let hw = seqByItem.get(key) || 0;
        if (serverSeq > hw) { hw = serverSeq; seqByItem.set(key, hw); }
        // Renumber a queued record whose seq no longer beats the server's.
        const recKey = recordKeyOf(section, module, it.position, type);
        const rec = records.get(recKey);
        if (rec && rec.clientSeq <= hw) {
          const bumped = hw + 1;
          seqByItem.set(key, bumped);
          records.set(recKey, { ...rec, clientSeq: bumped });
        }
      }
    }
  }

  // ── Recovery (requirement 2 + audit H4: persisted, survives terminal) ────────

  function toRecovery(recordKey) {
    const rec = records.get(recordKey);
    if (!rec) return;
    records.delete(recordKey);
    dirtyRecords.delete(recordKey); // it is no longer a pending mutation
    changedKeys.add(recordKey);     // the record changed (removed) + recovery grew
    recoveryDirty = true;

    // Store minimal identity only (no answer value — a closed module can't accept
    // it, and we minimize at-rest PII). Dedupe by slot+type.
    if (
      !recoveryItems.some(
        (x) => x.section === rec.section && x.module === rec.module
          && x.position === rec.position && x.type === rec.type,
      )
    ) {
      recoveryItems.push({
        section: rec.section, module: rec.module, position: rec.position, type: rec.type,
      });
    }
  }

  /** Remove a record whose module has CLOSED under it.
   *
   *  STUDENT WORK (answer/flag) → the persisted RECOVERY list: the student must be
   *  told, warmly, that an entry couldn't be saved before time expired (audit H4).
   *
   *  TELEMETRY (`time`, F13) → dropped SILENTLY, exactly like an EXAM_STALE_SEQ
   *  supersede. Recovering it would be a FALSE ALARM on the happy path: at every
   *  module close the final time report races the submit-module POST (both are
   *  issued in the same tick, and the finish drain deliberately does NOT wait for
   *  telemetry), so a recovered time record would routinely tell a student "a
   *  response couldn't be submitted before time expired" when every answer of theirs
   *  saved perfectly. The recovery banner's contract is the student's WORK; a few
   *  seconds of unreported dwell is not work, is not lost from their score, and
   *  self-heals on the next module anyway. */
  function retire(recordKey, rec) {
    if (rec.type === 'time') {
      dropRecord(recordKey, rec);
      return;
    }
    toRecovery(recordKey);
  }

  /** Remove a record from the queue outright — no recovery entry, no retry. The
   *  disposal used for TELEMETRY in every terminal situation (module closed, or a
   *  failed POST): a monotonic total is self-healing, so the next emit for that item
   *  carries everything this record did. */
  function dropRecord(recordKey, rec) {
    if (records.get(recordKey) !== rec) return;
    records.delete(recordKey);
    dirtyRecords.delete(recordKey);
    changedKeys.add(recordKey); // the queue changed → re-persist without it
  }

  // ── Flush (requirements 2 + 3) ──────────────────────────────────────────────

  function resetBackoff() {
    backoffMs = 0;
    if (retryTimer != null) {
      unschedule(retryTimer);
      retryTimer = null;
    }
  }

  function scheduleBackoff() {
    backoffMs = backoffMs === 0 ? BACKOFF_BASE_MS : Math.min(backoffMs * 2, BACKOFF_MAX_MS);
    if (retryTimer != null) unschedule(retryTimer);
    retryTimer = schedule(() => {
      retryTimer = null;
      flush();
    }, backoffMs);
  }

  function isOpenFor(rec) {
    return (
      serverOpen
      && serverOpen.status === 'in_progress'
      && serverOpen.section === rec.section
      && serverOpen.module === rec.module
    );
  }

  async function flush() {
    if (disposed || sending) return;
    if (!owns()) return; // a non-owning (secondary) tab does not flush (audit H1)
    // Server-authoritative (requirement 2): without a known server state we hold
    // (the caller fetches state first on reconnect, then calls setServerState).
    if (!serverOpen) return;
    if (records.size === 0) return;

    sending = true;
    try {
      // Drain in PASSES: sequential, in clientSeq order — one in-flight per session
      // guarantees per-item ordering (an older seq is never sent after a newer
      // one), so the server's client_seq check only fires on a genuine cross-tab /
      // retry race, never on our own coalesced re-answers. We re-scan after each
      // pass so a re-answer that COALESCED under an in-flight record (replacing it
      // with a higher seq) is still flushed — never stranded. Every record in a
      // non-empty pass is either sent, recovered, dropped, or triggers a return
      // (backoff), so `progressed` can only stay false when the queue is empty —
      // the loop always terminates.
      let progressed = true;
      while (progressed && !disposed && records.size > 0) {
        progressed = false;
        // Seq-less `time` records (F13) sort LAST — the student's WORK drains
        // first, always. Ordering telemetry ahead of answers would put a POST
        // that can fail (and, before it was made droppable, abort the pass)
        // between the student and their answer landing. `?? MAX_SAFE_INTEGER`
        // (not `|| 0`) is what puts them at the end without producing a NaN
        // comparator; per-item answer/flag order is unaffected (seqs are >= 1).
        const ordered = [...records.entries()].sort(
          (a, b) => (a[1].clientSeq ?? Number.MAX_SAFE_INTEGER)
            - (b[1].clientSeq ?? Number.MAX_SAFE_INTEGER),
        );
        for (const [key, rec] of ordered) {
          if (disposed) return;
          // Re-check: a prior iteration in this pass may already have handled this
          // key (coalesced), or the record may have been superseded.
          if (records.get(key) !== rec) continue;
          if (!isOpenFor(rec)) {
            // The module this record belongs to is no longer open (advanced /
            // terminal / break) → it couldn't be saved in time → recover it
            // (student work) or drop it silently (telemetry).
            retire(key, rec);
            progressed = true;
            continue;
          }
          try {
            await send(rec);
            // Success: remove ONLY if this exact record is still queued (a re-answer
            // during the await coalesces a newer record under the same key — don't
            // delete the newer one; the next pass sends it). A confirmed server send
            // also clears the dirty overlay + changed-key tracking (R3-H3 + R4-2).
            if (records.get(key) === rec) {
              records.delete(key); dirtyRecords.delete(key); changedKeys.delete(key);
            }
            resetBackoff();
            progressed = true;
          } catch (err) {
            const code = machineCode(err);
            if (code === STALE_SEQ_CODE) {
              // Superseded by a newer mutation → drop SILENTLY (not a failure).
              if (records.get(key) === rec) {
                records.delete(key); dirtyRecords.delete(key); changedKeys.delete(key);
              }
              progressed = true;
              continue;
            }
            if (STALE_MODULE_CODES.has(code)) {
              // The module/window closed → recover it (warm banner) if it is the
              // student's work; drop it silently if it is telemetry.
              retire(key, rec);
              progressed = true;
              continue;
            }
            if (rec.type === 'time') {
              // TELEMETRY FAILURE (429 / 422 / network / 5xx) → DROP it and keep
              // draining. This branch is the one that protects the student: the
              // records behind this one in the pass are their ANSWERS, and backing
              // the queue off here would strand them until the module closed. A
              // monotonic total needs no retry — the next emit for this item
              // already contains what this record carried — so dropping costs at
              // most a few seconds of pacing data and never a single answer.
              dropRecord(key, rec);
              progressed = true;
              continue;
            }
            // Network / 5xx / unknown → keep the record, back off, retry later.
            scheduleBackoff();
            persist();
            notify();
            return;
          }
        }
      }
      resetBackoff();
      persist();
      notify();
    } finally {
      sending = false;
    }
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  function enqueue(type, { section, module, position, value }) {
    // R3-H1: NEVER silently hold a mutation we cannot persist. A non-owner
    // (unresolved during the settle window, or a secondary) rejects — the shell
    // only renders the writable runner to a resolved OWNER, so this is a structural
    // belt against a mutation typed on a tab that can't durably save it. This gate
    // covers `time` records too (F13): a SECONDARY tab must never accrue or enqueue
    // active time — two tabs open on one exam would otherwise double-count the same
    // wall-clock seconds into one item.
    if (!owns()) return { durable: false, rejected: true };
    // `time` records carry no seq (max-merge needs no ordering — see the header).
    const clientSeq = type === 'time'
      ? undefined
      : nextSeq(section, module, position, type);
    const record = { type, section, module, position, value, clientSeq, createdAt: clock() };
    // Coalesce: one record per (slot, type) — a re-answer replaces the prior with
    // a higher seq (we only ever need to send the LATEST value for a slot).
    const recKey = recordKeyOf(section, module, position, type);
    records.set(recKey, record);
    changedKeys.add(recKey); // R4-2: this key changed since the last durable snapshot
    const durable = persist();
    notify();
    flush();
    return { durable, clientSeq, rejected: false };
  }

  load();

  return {
    /** Enqueue an answer. Returns { durable, clientSeq } — durable=false means the
     *  verified localStorage write failed (kept in memory; show the warm message). */
    enqueueAnswer({ section, module, position, answer }) {
      return enqueue('answer', { section, module, position, value: answer });
    },
    /** Enqueue a flag toggle. Same contract as enqueueAnswer. */
    enqueueFlag({ section, module, position, flagged }) {
      return enqueue('flag', { section, module, position, value: flagged });
    },
    /** Enqueue a per-item ACTIVE-TIME report (F13): `activeMsTotal` is the
     *  MONOTONIC cumulative total for that slot, not a delta. Coalesces per slot
     *  (one record per item — the newest total replaces the older), carries no
     *  seq, and never counts toward `pendingForOpen`. Same `{durable, rejected}`
     *  contract as the other enqueues; a non-owner is REJECTED (a secondary tab
     *  never accrues). A non-finite/negative total is ignored outright — a bad
     *  clock reading must never enter the durable queue. */
    enqueueItemTime({ section, module, position, activeMsTotal }) {
      // A non-number / NaN / Infinity has no clamp that means anything — reject.
      if (typeof activeMsTotal !== 'number' || !Number.isFinite(activeMsTotal)) {
        return { durable: false, rejected: true };
      }
      // CLAMP both ends into the server's accepted range rather than queueing a
      // record that is guaranteed to 422. A negative can only come from a broken
      // clock; an over-ceiling value from a corrupt seed. Neither should cost the
      // student a wasted request, and neither should sit in the durable queue.
      const total = Math.min(
        Math.max(0, Math.floor(activeMsTotal)), MAX_REPORTED_ACTIVE_MS,
      );
      return enqueue('time', { section, module, position, value: total });
    },
    /** Feed the latest server state (from every /state or transition response).
     *  Seeds the per-item seq high-water mark from the manifest, updates the open
     *  module, and triggers a flush (server-authoritative). */
    setServerState(state) {
      if (!state) return;
      serverOpen = {
        status: state.status,
        section: state.section ?? null,
        module: state.module ?? null,
      };
      seedFromServerItems(state.items);
      persist();
      notify();
      flush();
    },
    /** Force a flush attempt (the caller wires window 'online' → refetch state →
     *  setServerState, which already flushes; this is the direct hook for tests /
     *  belt-and-suspenders). */
    flush,
    /** On ownership PROMOTION (secondary → owner): REPLACE in-memory state with the
     *  latest storage snapshot (R2-M4 — never merge stale, already-flushed records),
     *  THEN persist as the new owner. The caller refetches server state next
     *  (→ setServerState reseeds + flushes). */
    acquireOwnership() {
      reconcileFromStorage();
      persist();
      notify();
    },
    /** The student acknowledged the recovery notice ("Got it"): clear the
     *  persisted recovery. If nothing else remains, persist() removes the key. */
    dismissRecovery() {
      recoveryItems = [];
      recoveryDirty = true; // recovery changed since the last durable snapshot
      persist();
      notify();
    },
    /** Purge this session's outbox — memory + storage (a full wipe). Used by the
     *  logout path via purgeAllExamOutboxes; NOT called on terminal (unacknowledged
     *  recovery must survive — audit H4). Storage removal is owner-gated so a
     *  secondary tab can't clobber the shared key. */
    purge() {
      records = new Map();
      seqByItem = new Map();
      recoveryItems = [];
      dirtyRecords = new Map();
      dirtyRecovery = [];
      changedKeys = new Set();
      recoveryDirty = false;
      resetBackoff();
      if (owns()) {
        try { if (store) store.removeItem(storageKey); } catch { /* best-effort */ }
      }
      notify();
    },
    /** Current snapshot for the UI (status chip + submit gating + recovery banner). */
    getSnapshot,
    /** Stop timers + detach (unmount). Does NOT purge (progress must survive a
     *  navigation away that is not a logout / terminal state). */
    dispose() {
      disposed = true;
      resetBackoff();
    },
    // Test seams.
    _records: () => records,
    _seqByItem: () => seqByItem,
    _recovery: () => recoveryItems,
    _dirty: () => dirtyRecords,
  };
}

/**
 * Purge EVERY exam outbox on the device (all users, all sessions). The
 * shared-device wipe hooked into logout (requirement 5): logout does not know the
 * active session id, and handing a device to another user must leave no minor's
 * queued responses behind, so we reap the whole `v1` prefix. Best-effort + safe
 * on a storage that throws.
 */
export function purgeAllExamOutboxes(storage) {
  const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  if (!store) return;
  for (const key of storageKeys(store)) {
    if (typeof key === 'string' && key.startsWith(OUTBOX_KEY_PREFIX)) {
      try { store.removeItem(key); } catch { /* best-effort */ }
    }
  }
}
